import 'chartjs-adapter-date-fns'

import { cn } from '@sparkai/utils'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Bar, getElementsAtEvent } from 'react-chartjs-2'

import { BarChartPluginsType, type BarChartProps, ChartValueClickEventProps } from '../Charts.types'
import {
  DAILY_BAR_OPTIONS,
  deepMergeChartOptions,
  DEFAULT_BAR_OPTIONS,
  CURRENT_DAY_BAR_OPTIONS,
  WEEKLY_BAR_OPTIONS,
} from '../configs/options'
import { LABEL_WITH_IMAGE_PLUGIN, PREDICTION_BAR_PLUGIN } from '../configs/plugins'

ChartJS.register(CategoryScale, LinearScale, TimeScale, BarElement, Title, Tooltip, Legend)

function determineChartType(option: string) {
  switch (option) {
    case 'daily':
      return DAILY_BAR_OPTIONS
    case 'weekly':
      return WEEKLY_BAR_OPTIONS()
    case 'current-day-engagements':
      return CURRENT_DAY_BAR_OPTIONS
    default:
      return DEFAULT_BAR_OPTIONS
  }
}

function addPlugins(plugins: Array<BarChartPluginsType> | undefined) {
  if (!plugins) return []

  const values = {
    'label-with-image': LABEL_WITH_IMAGE_PLUGIN,
    'prediction-bar': PREDICTION_BAR_PLUGIN,
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const list: any[] = []
  plugins.forEach((plugin) => list.push(values[plugin]))

  return list
}

export function BarChart({
  className = '',
  labels,
  datasets,
  onClick,
  option = 'default',
  optionOverride,
  customTooltip = null,
  onValueClick,
  usesMonospacedFontOnAxis = false,
  plugins,
  multipleTooltip,
}: BarChartProps) {
  const classNames = className
  const chartRef = useRef()
  const _data = { labels, datasets } as unknown as ChartData<'bar'>

  const _options = useMemo(() => {
    const selectedOption = {
      ...determineChartType(option),
      onClick: (event: ChartValueClickEventProps, ctx?: { index?: number }[]) => {
        if (ctx && ctx.length > 0 && ctx[0].index !== undefined) {
          const label = event.chart.config.data.labels[ctx[0].index]
          onValueClick && onValueClick(label)
        }
      },
    }

    const opt = Object.assign({}, selectedOption)

    if (usesMonospacedFontOnAxis) {
      const ycallback = opt.scales?.y?.ticks?.callback
      opt.scales = {
        ...opt.scales,
        x: {
          ...opt.scales?.x,
          ticks: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(opt.scales?.x as any)?.ticks,
            font: { family: 'Roboto Mono' },
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        y: {
          ...opt.scales?.y,
          ticks: {
            callback: (tickValue: number) => {
              return ycallback(tickValue).padStart(5, ' ')
            },
            font: { family: 'Roboto Mono' },
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      }
    }
    return opt as unknown as ChartOptions
  }, [onValueClick, option, usesMonospacedFontOnAxis])

  const referencePlugins = addPlugins(plugins)

  const [showCustom, setShowCustom] = useState(false)

  if (customTooltip) {
    const base = _options?.plugins ?? {}
    _options.plugins = { ...base, tooltip: { enabled: false } }
  }

  const toggleCustomTooltip = useCallback(
    () => (customTooltip || multipleTooltip) && setShowCustom(!showCustom),
    [customTooltip, multipleTooltip, showCustom]
  )

  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (chartRef?.current && onClick) {
        onClick(getElementsAtEvent(chartRef?.current, event))
      }
    },
    [onClick]
  )

  const finalOptions = deepMergeChartOptions<'bar'>(
    _options as unknown as ChartOptions<'bar'>,
    optionOverride
  )

  return (
    <>
      <Bar
        className={cn('h-[232px] max-h-[232px]', classNames, {
          'cursor-pointer': !!onClick,
          relative: customTooltip || multipleTooltip,
        })}
        ref={chartRef}
        options={finalOptions as unknown as ChartOptions<'bar'>}
        data={_data}
        onClick={clickHandler}
        onMouseEnter={toggleCustomTooltip}
        onMouseLeave={toggleCustomTooltip}
        plugins={referencePlugins}
      />
      {customTooltip && showCustom && customTooltip}
      {multipleTooltip && showCustom && multipleTooltip}
    </>
  )
}
